import React, { useState } from "react";
import { images } from "../../Assets";
import { Button } from "../../Components/ui/button";
import { Modal, ModalContent } from "../../Components/ui/modal";
import {
  useGetIndividualOrderQuery,
  useCancelOrderMutation,
} from "../Orders/OrderApi";
import { format, parseISO } from "date-fns";
import { SkeletonTable } from "../../Components/ui/skeleton";
import Stepper from "../Dashboard/Stepper";
import Spinner from "../../Assets/spinner";
import toast from "react-hot-toast";
import { parseMoney, writeClipboardText } from "../../Lib/utils";

const STATUS_VARIANT_MAP = {
  pending: "pending",
  accepted: "accepted",
  picked_up: "picked_up",
  in_progress: "in_progress",
  delivered: "delivered",
  cancelled: "cancelled",
  declined: "declined",
  expired: "expired",
};

const PAYMENT_VARIANT_MAP = {
  paid: "successful",
  unpaid: "pending",
};

const CANCELLABLE_STATUSES = ["pending", "accepted"];

const Field = ({ label, children }) => (
  <div>
    <p className='text-xs text-gray-600 font-semibold'>{label}</p>
    <p className='text-primaryBlack font-normal text-sm break-words'>
      {children}
    </p>
  </div>
);

const OrderDetails = ({ id }) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const { data, isLoading } = useGetIndividualOrderQuery(id, {
    refetchOnFocus: true,
  });
  const [cancelOrder, { isLoading: cancelling }] = useCancelOrderMutation();

  const order = data?.data;

  const handleCancel = async () => {
    try {
      const res = await cancelOrder(id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Order cancelled", { id: "orderCancel" });
        setCancelOpen(false);
      }
    } catch {
      toast.error("Action failed, please try again", { id: "orderErr" });
    }
  };

  if (isLoading)
    return (
      <div className='w-full'>
        <SkeletonTable />
      </div>
    );

  const statusVariant = STATUS_VARIANT_MAP[order?.currentStatus] || "pending";
  const paymentVariant = PAYMENT_VARIANT_MAP[order?.paymentStatus] || "pending";

  const statusSteps = (order?.status ?? [])
    .slice()
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .map((s) => ({
      label: s.name.replace(/_/g, " "),
      time: s.createdAt ? format(parseISO(s.createdAt), "p, MMM d") : "",
    }));

  return (
    <div className='w-full'>
      <div className='grid md:grid-cols-3 gap-5 p-2'>
        <Field label='Order Date and Time'>
          {order?.createdAt
            ? format(parseISO(order.createdAt), "p, MMM d, yyyy")
            : "N/A"}
        </Field>
        <Field label='Item'>{order?.item || "N/A"}</Field>
        <Field label='Status'>
          <Button asChild variant={statusVariant} size='sm'>
            <p className='capitalize'>
              {order?.currentStatus
                ? order.currentStatus.replace(/_/g, " ")
                : "N/A"}
            </p>
          </Button>
        </Field>
        <Field label='Amount'>{parseMoney(order?.amount)}</Field>
        <Field label='Rider Fee'>{parseMoney(order?.riderFee ?? 0)}</Field>
        <Field label='Vendor Fee'>{parseMoney(order?.vendorFee ?? 0)}</Field>
        <Field label='Service Fee'>{parseMoney(order?.serviceFee ?? 0)}</Field>
        <Field label='Vehicle'>
          <p className='capitalize'>{order?.vehicleType || "N/A"}</p>
        </Field>
        <Field label='Payment Status'>
          <Button asChild variant={paymentVariant} size='sm'>
            <p className='capitalize'>{order?.paymentStatus || "N/A"}</p>
          </Button>
        </Field>
        <Field label='Channel'>
          <span className='capitalize'>
            {order?.channel?.replace(/_/g, " ") || "N/A"}
          </span>
        </Field>
      </div>

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-1'>
          Payment Details
        </p>
        <div className='grid md:grid-cols-3 gap-5'>
          <Field label='Payment Reference'>
            {order?.paymentReference || "N/A"}
          </Field>
          <Field label='Payment Gateway'>
            <span className='capitalize'>{order?.paymentGateway || "N/A"}</span>
          </Field>
          <Field label='Paid Amount'>
            {order?.paidAmount != null ? parseMoney(order.paidAmount) : "N/A"}
          </Field>
          <Field label='Paid At'>
            {order?.paidAt
              ? format(parseISO(order.paidAt), "p, MMM d, yyyy")
              : "N/A"}
          </Field>
        </div>
      </div>

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <Field label='Delivery Code'>
          <Button
            variant='ghost'
            className='lowercase flex items-center text-center gap-1 text-primaryBlack font-bold px-0'
            onClick={() =>
              writeClipboardText("OTP copied successfuly", order?.deliveryCode)
            }
          >
            {order?.deliveryCode} <img src={images.copy} alt='copy' />
          </Button>
        </Field>
      </div>

      <div className='rounded-md border border-gray-100 p-3 mt-4'>
        <div className='flex gap-2 pb-3 border-b border-gray-100'>
          <img src={images.pickup} alt='pickup' />
          <Field label='Pick up'>
            {order?.pickupLocation?.address || "N/A"}
          </Field>
        </div>
        <div className='flex gap-2 pt-3'>
          <img src={images.dropOff} alt='dropOff' />
          <Field label='Drop off'>{order?.deliveryAddress || "N/A"}</Field>
        </div>
      </div>

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-1'>
          Customer Details
        </p>
        <div className='grid md:grid-cols-3 gap-5'>
          <Field label='Name'>
            <span className='capitalize'>{order?.customerName || "N/A"}</span>
          </Field>
          <Field label='Phone Number'>
            {order?.customerPhoneNumber || "N/A"}
          </Field>
        </div>
      </div>

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-1'>
          Recipient Details
        </p>
        <div className='grid md:grid-cols-3 gap-5'>
          <Field label='Name'>
            <span className='capitalize'>{order?.recipientName || "N/A"}</span>
          </Field>
          <Field label='Phone Number'>
            {order?.recipientPhoneNumber || "N/A"}
          </Field>
        </div>
      </div>

      {order?.extraDeliveryNote && (
        <div className='border-t border-gray-100 mt-4 pt-4'>
          <p className='text-xs text-gray-600 font-semibold mb-1'>
            Delivery Note
          </p>
          <p className='text-primaryBlack font-normal text-sm break-words'>
            {order.extraDeliveryNote}
          </p>
        </div>
      )}

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-1'>
          Rider Details
        </p>
        <div className='grid md:grid-cols-3 gap-5'>
          <Field label='Name'>
            {order?.rider
              ? `${order.rider.firstName || ""} ${order.rider.lastName || ""}`.trim()
              : "N/A"}
          </Field>
          <Field label='Phone Number'>
            {order?.rider?.phoneNumber || "N/A"}
          </Field>
        </div>
      </div>
      {statusSteps.length > 0 && (
        <div className='border-t border-gray-100 mt-4 pt-4'>
          <p className='text-xs text-gray-600 font-semibold mb-1'>
            Status Trail
          </p>
          <Stepper steps={statusSteps} />
        </div>
      )}

      {CANCELLABLE_STATUSES.includes(order?.currentStatus) && (
        <div className='border-t border-gray-100 mt-4 pt-4 flex justify-end gap-2'>
          <Button variant='failed' onClick={() => setCancelOpen(true)}>
            Cancel Order
          </Button>
        </div>
      )}

      <Modal open={cancelOpen} onOpenChange={setCancelOpen}>
        <ModalContent name='Cancel Order?'>
          <p className='text-sm text-primaryBlack my-4'>
            Are you sure you want to cancel this order? This action cannot be
            undone.
          </p>
          <div className='flex justify-between items-center bg-white w-[100%]'>
            <Button
              variant='successful'
              className='w-[30%]'
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? <Spinner /> : "Yes"}
            </Button>
            <Button
              variant='failed'
              className='w-[30%]'
              onClick={() => setCancelOpen(false)}
            >
              No
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default OrderDetails;
